import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';
import SEOHead from '../../components/base/SEOHead';

export default function CancellationPage() {
  const { t } = useTranslation();
  
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <SEOHead
        title={t('seo.cancellation.title')}
        description={t('seo.cancellation.description')}
        keywords={t('seo.cancellation.keywords')}
      />
      
      <div className="min-h-screen bg-gradient-to-br from-teal-50 via-white to-teal-50">
        <Navbar />
        
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-32">
          {/* Hlavička */}
          <div className="text-center mb-16">
            <h1 className="text-5xl font-bold text-gray-900 mb-4">
              Storno podmínky
            </h1>
            <p className="text-xl text-gray-600">
              Pravidla pro zrušení objednávky
            </p>
            <p className="text-sm text-gray-500 mt-4">
              Platné od: 1. ledna 2025
            </p>
          </div>

          {/* Obsah */}
          <div className="bg-white rounded-2xl shadow-lg p-8 lg:p-12 space-y-8">
            
            {/* Úvod */}
            <section>
              <div className="flex items-start gap-4 mb-4">
                <div className="w-12 h-12 bg-pink-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <i className="ri-information-line text-2xl text-pink-600"></i>
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-gray-900 mb-3">
                    Obecné informace
                  </h2>
                  <p className="text-gray-700 leading-relaxed">
                    Tyto storno podmínky upravují pravidla pro zrušení objednávky služeb žehlení prádla poskytovaných společností VYŽEHLENÉ. Objednávka je považována za závaznou od okamžiku jejího potvrzení ze strany poskytovatele služby.
                  </p>
                </div>
              </div>
            </section>

            {/* Storno více než 24h */}
            <section className="border-t-2 border-gray-100 pt-8">
              <div className="flex items-start gap-4 mb-4">
                <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <i className="ri-checkbox-circle-line text-2xl text-green-600"></i>
                </div>
                <div className="flex-1">
                  <h2 className="text-2xl font-bold text-gray-900 mb-3">
                    Storno více než 24 hodin před svozem
                  </h2>
                  <div className="bg-green-50 border-2 border-green-200 rounded-lg p-6 mb-4">
                    <p className="text-lg font-bold text-green-800 mb-2">
                      ✅ Zrušení ZDARMA
                    </p>
                    <p className="text-gray-700">
                      Pokud zrušíte objednávku více než 24 hodin před plánovaným svozem prádla, storno je zcela zdarma a neúčtujeme žádné poplatky.
                    </p>
                  </div>
                  
                  <div className="space-y-3">
                    <h3 className="font-bold text-gray-900">Jak zrušit objednávku:</h3>
                    <ul className="space-y-2">
                      <li className="flex items-start gap-3">
                        <i className="ri-phone-line text-pink-600 flex-shrink-0 mt-1"></i>
                        <div>
                          <strong>Telefonicky:</strong>
                          <p className="text-gray-600">Praha: +420 776 166 733</p>
                          <p className="text-gray-600">Stará Boleslav: +420 603 290 573</p>
                        </div>
                      </li>
                      <li className="flex items-start gap-3">
                        <i className="ri-mail-line text-pink-600 flex-shrink-0 mt-1"></i>
                        <div>
                          <strong>Emailem:</strong>
                          <p className="text-gray-600">Kontaktujte nás prostřednictvím kontaktního formuláře na webu</p>
                        </div>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </section>

            {/* Storno méně než 24h */}
            <section className="border-t-2 border-gray-100 pt-8">
              <div className="flex items-start gap-4 mb-4">
                <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <i className="ri-alert-line text-2xl text-red-600"></i>
                </div>
                <div className="flex-1">
                  <h2 className="text-2xl font-bold text-gray-900 mb-3">
                    Storno méně než 24 hodin před svozem
                  </h2>
                  <div className="bg-red-50 border-2 border-red-200 rounded-lg p-6 mb-4">
                    <p className="text-lg font-bold text-red-800 mb-2">
                      ⚠️ Storno poplatek: 100% z ceny objednávky
                    </p>
                    <p className="text-gray-700">
                      Pokud zrušíte objednávku méně než 24 hodin před plánovaným svozem, účtujeme storno poplatek ve výši 100% z celkové ceny objednávky včetně dopravy.
                    </p>
                  </div>
                  
                  <div className="space-y-3">
                    <h3 className="font-bold text-gray-900">Důvod storno poplatku:</h3>
                    <ul className="space-y-2 text-gray-700">
                      <li className="flex items-start gap-3">
                        <i className="ri-time-line text-teal-600 flex-shrink-0 mt-1"></i>
                        <span>Máme již naplánovanou trasu a vyhrazený čas pro svoz vašeho prádla</span>
                      </li>
                      <li className="flex items-start gap-3">
                        <i className="ri-calendar-line text-teal-600 flex-shrink-0 mt-1"></i>
                        <span>Nemůžeme v tak krátkém čase obsadit volnou kapacitu jiným zákazníkem</span>
                      </li>
                      <li className="flex items-start gap-3">
                        <i className="ri-truck-line text-teal-600 flex-shrink-0 mt-1"></i>
                        <span>Vznikají nám náklady na dopravu a organizaci</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </section>

            {/* Změna termínu */}
            <section className="border-t-2 border-gray-100 pt-8">
              <div className="flex items-start gap-4 mb-4">
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <i className="ri-calendar-check-line text-2xl text-blue-600"></i>
                </div>
                <div className="flex-1">
                  <h2 className="text-2xl font-bold text-gray-900 mb-3">
                    Změna termínu svozu
                  </h2>
                  <p className="text-gray-700 leading-relaxed mb-4">
                    Pokud potřebujete změnit termín svozu, kontaktujte nás co nejdříve. Změna termínu je možná bez poplatku, pokud je provedena více než 24 hodin před původním termínem.
                  </p>
                  
                  <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-4">
                    <p className="text-sm text-gray-700">
                      <strong>Tip:</strong> Změna termínu je vždy lepší než zrušení objednávky. Rádi vám vyhovíme a najdeme nový vhodný termín.
                    </p>
                  </div>
                </div>
              </div>
            </section>

            {/* Výjimečné situace */}
            <section className="border-t-2 border-gray-100 pt-8">
              <div className="flex items-start gap-4 mb-4">
                <div className="w-12 h-12 bg-teal-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <i className="ri-heart-line text-2xl text-teal-600"></i>
                </div>
                <div className="flex-1">
                  <h2 className="text-2xl font-bold text-gray-900 mb-3">
                    Výjimečné situace
                  </h2>
                  <p className="text-gray-700 leading-relaxed mb-4">
                    Chápeme, že mohou nastat nepředvídatelné situace. V případě vážných důvodů (nemoc, nehoda, vyšší moc) jsme připraveni individuálně posoudit možnost zrušení objednávky bez storno poplatku.
                  </p>
                  
                  <div className="space-y-2">
                    <p className="text-sm text-gray-600">
                      <strong>Příklady výjimečných situací:</strong>
                    </p>
                    <ul className="space-y-1 text-sm text-gray-600">
                      <li className="flex items-start gap-2">
                        <i className="ri-arrow-right-s-line text-teal-600 flex-shrink-0 mt-0.5"></i>
                        <span>Náhlé zdravotní komplikace</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <i className="ri-arrow-right-s-line text-teal-600 flex-shrink-0 mt-0.5"></i>
                        <span>Dopravní nehoda</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <i className="ri-arrow-right-s-line text-teal-600 flex-shrink-0 mt-0.5"></i>
                        <span>Živelní pohromy</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </section>

            {/* Storno ze strany poskytovatele */}
            <section className="border-t-2 border-gray-100 pt-8">
              <div className="flex items-start gap-4 mb-4">
                <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <i className="ri-shield-check-line text-2xl text-purple-600"></i>
                </div>
                <div className="flex-1">
                  <h2 className="text-2xl font-bold text-gray-900 mb-3">
                    Storno ze strany poskytovatele
                  </h2>
                  <p className="text-gray-700 leading-relaxed mb-4">
                    V případě, že budeme nuceni zrušit objednávku z naší strany (technické problémy, nemoc zaměstnance, vyšší moc), budeme vás okamžitě kontaktovat a nabídneme náhradní termín nebo plnou refundaci případné zálohy.
                  </p>
                  
                  <div className="bg-purple-50 border-2 border-purple-200 rounded-lg p-4">
                    <p className="text-sm text-gray-700">
                      <strong>Záruka:</strong> Pokud zrušíme objednávku my, neúčtujeme žádné poplatky a případnou zálohu vám vrátíme v plné výši do 3 pracovních dnů.
                    </p>
                  </div>
                </div>
              </div>
            </section>

            {/* Kontakt */}
            <section className="border-t-2 border-gray-100 pt-8">
              <div className="bg-gradient-to-br from-pink-50 to-teal-50 rounded-xl p-6">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">
                  Máte dotazy ke storno podmínkám?
                </h2>
                <p className="text-gray-700 mb-4">
                  Jsme tu pro vás a rádi vám vše vysvětlíme. Neváhejte nás kontaktovat.
                </p>
                <div className="grid md:grid-cols-2 gap-4">
                  <a href="tel:+420776166733" className="flex items-center gap-3 bg-white rounded-lg p-4 hover:shadow-md transition-shadow cursor-pointer">
                    <i className="ri-phone-fill text-2xl text-pink-600"></i>
                    <div>
                      <p className="text-sm text-gray-600">Praha</p>
                      <p className="font-bold text-gray-900">+420 776 166 733</p>
                    </div>
                  </a>
                  <a href="tel:+420603290573" className="flex items-center gap-3 bg-white rounded-lg p-4 hover:shadow-md transition-shadow cursor-pointer">
                    <i className="ri-phone-fill text-2xl text-teal-600"></i>
                    <div>
                      <p className="text-sm text-gray-600">Stará Boleslav</p>
                      <p className="font-bold text-gray-900">+420 603 290 573</p>
                    </div>
                  </a>
                </div>
              </div>
            </section>

          </div>

          {/* Zpět na hlavní stránku */}
          <div className="text-center mt-12">
            <button
              onClick={() => window.REACT_APP_NAVIGATE('/')}
              className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-pink-600 to-pink-500 text-white rounded-lg font-semibold hover:from-pink-700 hover:to-pink-600 transition-all shadow-lg whitespace-nowrap cursor-pointer"
            >
              <i className="ri-arrow-left-line text-xl"></i>
              Zpět na hlavní stránku
            </button>
          </div>
        </div>

        <Footer />
      </div>
    </>
  );
}
